import * as React from 'react'
import { Eye, EyeOff } from 'lucide-react'
import { sb } from '@/lib/supabase'
import { useAuth } from '@/auth/auth-provider'
import type { Activity, ActivityKind, ActivityStatus } from '@/lib/types'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { KIND_META } from '@/pages/activities'
import { useDirty, useDiscardGuard } from '@/hooks/use-unsaved'
import { DiscardPrompt } from '@/components/discard-prompt'

const STATUSES: ActivityStatus[] = ['planned', 'in_progress', 'complete', 'skipped']

export function ActivityEditor({
  teamId,
  activity,
  onClose,
  onSaved,
}: {
  teamId: string
  activity?: Activity
  onClose: () => void
  onSaved: () => void
}) {
  const [title, setTitle] = React.useState(activity?.title ?? '')
  const [summary, setSummary] = React.useState(activity?.summary ?? '')
  const [kind, setKind] = React.useState<ActivityKind>(activity?.kind ?? 'presentation')
  const [status, setStatus] = React.useState<ActivityStatus>(activity?.status ?? 'planned')
  const [presenter, setPresenter] = React.useState(activity?.presenter ?? '')
  const [duration, setDuration] = React.useState(
    activity?.duration_minutes != null ? String(activity.duration_minutes) : '',
  )
  // <input type="datetime-local"> needs "YYYY-MM-DDTHH:mm" in LOCAL time, while
  // the column is timestamptz. Convert both ways rather than slicing the ISO
  // string, which would silently shift everything by the UTC offset.
  const [scheduled, setScheduled] = React.useState(toLocalInput(activity?.scheduled_for))
  const [content, setContent] = React.useState(activity?.content ?? '')
  const [exercise, setExercise] = React.useState(activity?.exercise ?? '')
  // A new topic is visible: somebody sitting here writing one means the team to
  // read it. Only the seeded curriculum arrives hidden, and only a coach can put
  // a topic back into that state — RLS refuses `visible: false` from a lead, so
  // the control is coach-only rather than merely styled that way.
  const [visible, setVisible] = React.useState(activity?.visible ?? true)
  const [busy, setBusy] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)

  const dirty = useDirty({
    title, summary, kind, status, presenter, duration, scheduled, content, exercise, visible,
  })
  const guard = useDiscardGuard(dirty, onClose)
  const { isCoach } = useAuth()

  async function save(e: React.FormEvent) {
    e.preventDefault()
    if (!title.trim()) return setError('Give the topic a title.')
    const mins = duration.trim() === '' ? null : Number(duration)
    if (mins != null && (!Number.isFinite(mins) || mins <= 0)) {
      return setError('Duration must be a positive number of minutes.')
    }

    setBusy(true)
    setError(null)
    try {
      const row = {
        team_id: teamId,
        title: title.trim(),
        summary: summary.trim() || null,
        kind,
        status,
        presenter: presenter.trim() || null,
        duration_minutes: mins,
        scheduled_for: scheduled ? new Date(scheduled).toISOString() : null,
        content: content.trim() || null,
        exercise: exercise.trim() || null,
        visible,
      }
      const { error: err } = activity
        ? await sb().from('activities').update(row).eq('id', activity.id)
        : await sb().from('activities').insert(row)
      if (err) throw err
      onSaved()
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog open onOpenChange={(o) => !o && guard.requestClose()}>
      <DialogContent className="sm:max-w-2xl">
        <DiscardPrompt guard={guard} what="this topic" />
        <form onSubmit={save}>
          <DialogHeader>
            <DialogTitle>{activity ? 'Edit topic' : 'New topic'}</DialogTitle>
            <DialogDescription>
              A session plus the exercise that follows it. Notes and instructions accept Markdown.
            </DialogDescription>
          </DialogHeader>

          <div className="my-4 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="title">Title</Label>
              <Input id="title" value={title} onChange={(e) => setTitle(e.target.value)} autoFocus />
            </div>

            <div className="space-y-2">
              <Label htmlFor="summary">Summary</Label>
              <Textarea
                id="summary"
                rows={2}
                value={summary}
                onChange={(e) => setSummary(e.target.value)}
                placeholder="One or two lines — what this covers and why"
              />
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="kind">Type</Label>
                <Select value={kind} onValueChange={(v) => setKind(v as ActivityKind)}>
                  <SelectTrigger id="kind">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(Object.keys(KIND_META) as ActivityKind[]).map((k) => (
                      <SelectItem key={k} value={k}>
                        {KIND_META[k].label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="status">Status</Label>
                <Select value={status} onValueChange={(v) => setStatus(v as ActivityStatus)}>
                  <SelectTrigger id="status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUSES.map((s) => (
                      <SelectItem key={s} value={s}>
                        {s === 'in_progress' ? 'In progress' : s[0].toUpperCase() + s.slice(1)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-3">
              <div className="space-y-2 sm:col-span-1">
                <Label htmlFor="presenter">Led by</Label>
                <Input
                  id="presenter"
                  value={presenter}
                  onChange={(e) => setPresenter(e.target.value)}
                  placeholder="Name"
                />
              </div>
              <div className="space-y-2 sm:col-span-1">
                <Label htmlFor="duration">Minutes</Label>
                <Input
                  id="duration"
                  inputMode="numeric"
                  value={duration}
                  onChange={(e) => setDuration(e.target.value)}
                  placeholder="60"
                />
              </div>
              <div className="space-y-2 sm:col-span-1">
                <Label htmlFor="scheduled">Scheduled</Label>
                <Input
                  id="scheduled"
                  type="datetime-local"
                  value={scheduled}
                  onChange={(e) => setScheduled(e.target.value)}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="content">Session notes</Label>
              <Textarea
                id="content"
                rows={5}
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder={'## Key points\n- Markdown works here\n- Use **bold**, `code`, and [links](https://example.com)'}
                className="font-mono text-xs"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="exercise">The activity</Label>
              <Textarea
                id="exercise"
                rows={4}
                value={exercise}
                onChange={(e) => setExercise(e.target.value)}
                placeholder={'What the team actually does after the talk.\n\n1. Pair up\n2. …'}
                className="font-mono text-xs"
              />
            </div>

            {/* Coaches only. A lead who flipped this would hide the topic from
                themselves, and the database refuses it regardless (0019). */}
            {isCoach && (
              <div className="flex items-start justify-between gap-4 rounded-md border p-3">
                <div className="space-y-0.5">
                  <Label htmlFor="visible" className="flex items-center gap-1.5">
                    {visible ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
                    Visible to the team
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    {visible
                      ? 'Everyone on the team can see this topic.'
                      : 'Hidden from members and the team lead — only DPL sees it. Presenting it makes it visible.'}
                  </p>
                </div>
                <Switch id="visible" checked={visible} onCheckedChange={setVisible} className="mt-1" />
              </div>
            )}

            {error && <p className="text-sm text-destructive">{error}</p>}
          </div>

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={guard.requestClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={busy}>
              {activity ? 'Save changes' : 'Create topic'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

/** timestamptz → the local "YYYY-MM-DDTHH:mm" that datetime-local expects. */
function toLocalInput(iso: string | null | undefined): string {
  if (!iso) return ''
  const d = new Date(iso)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

export { toLocalInput }
